import { BaseIcon, Button, Icon, Tag, TextButton, Trigger, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AddressOptionResource } from "../../../../shared/types/address";
import { contactService } from "../../shared/api/contactService";
import { IContactCompanyFormState, IContactUpdateResource } from "../../shared/types";
import {
	DrawerFormButtonContainer,
	MarkAsPrimaryButton,
	PrimaryLabel,
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionItem,
	ViewPageSectionListItem,
} from "../../shared/view";
import { ValueWrapper } from "../../shared/view/ViewComponents";
import { ContactCompanyDrawer } from "./ContactCompanyDrawer";

const StyledViewPageSection = styled(ViewPageSection)`
	grid-area: companies;
`;

const CompanyWrapper = styled.div`
	display: grid;
	gap: 2px;
`;

const StyledPrimaryLabel = styled(PrimaryLabel)`
	align-self: center;
`;

const TagRow = styled.div`
	display: flex;
	gap: 4px;
	flex-wrap: wrap;
	align-items: center;
`;

const StatusIcon = styled(BaseIcon)`
	display: inline-block;
	width: 1em;
	height: 1em;
	vertical-align: middle;
	margin-right: 4px;
	background: ${({ theme }) => theme.colors.palette.white};
`;

const formatAddress = (addr: AddressOptionResource) =>
	[[addr.street, addr.houseNumber].filter(Boolean).join(" "), [addr.zip, addr.city].filter(Boolean).join(" ")]
		.filter(Boolean)
		.join(", ");

export const ContactViewCompaniesSection: FC<{ contact: IContactUpdateResource }> = ({ contact }) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const emptyContactCompany: IContactCompanyFormState = {
		position: "",
		company: null,
		isEvent: false,
		isCarFleet: false,
	};
	const onClosed = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: contactService.listKey });
		void queryClient.invalidateQueries({ queryKey: contactService.showKey(contact.id) });
	}, [contact.id, queryClient]);
	const { showToast } = useToasts();

	return (
		<StyledViewPageSection>
			<ViewPageSectionHeader>{t("Companies")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				{contact.companies.map((x) => (
					<Trigger
						key={x.id}
						render={(props) => (
							<ContactCompanyDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Edit company")}
								initialValues={{
									position: x.position,
									isEvent: x.isEvent,
									isCarFleet: x.isCarFleet,
									company: x,
								}}
								onClosed={onClosed}
								onSubmit={async (values) => {
									if (!values.company) throw "Missing validation for field: company";
									const res = await contactService.attachCompany(contact.id, values.company.id, {
										position: values.position,
										isEvent: values.isEvent,
										isCarFleet: values.isCarFleet,
									});
									if (isSuccess(res)) {
										showToast({
											body: t("Company was saved."),
											title: t("Company"),
											type: "success",
										});
									}
									return isSuccess(res);
								}}
								disableCompanySelect={true}
								companyId={x.id}
							>
								<DrawerFormButtonContainer>
									{!x.isPrimary && (
										<MarkAsPrimaryButton
											entityId={x.id}
											markAsPrimary={(companyId) => contactService.markCompanyAsPrimary(companyId, contact.id)}
											onSubmit={() => {
												void queryClient.invalidateQueries({
													predicate: (query) => query.queryKey.includes("clipping-provider-contacts"),
												});
												props.handleClose();
											}}
										/>
									)}
									<Button
										type="button"
										color="danger"
										onClick={async () => {
											const res = await contactService.detachCompany(contact.id, x.id);
											if (isSuccess(res)) {
												showToast({
													body: t("Company was detached."),
													title: t("Company"),
													type: "success",
												});
												props.handleClose();
											}
										}}
									>
										{t("Detach company")}
									</Button>
								</DrawerFormButtonContainer>
							</ContactCompanyDrawer>
						)}
					>
						{(props) => (
							<ViewPageSectionListItem {...props}>
								<ValueWrapper>
									<CompanyWrapper>
										<span>{x.name}</span>
										{x.position && <span>{x.position}</span>}
										{x.primaryAddress && <span>{formatAddress(x.primaryAddress)}</span>}
										<TagRow>
											<Tag size="small" title={x.isEvent ? t("Is an event contact") : t("Is not an event contact")}>
												<StatusIcon src={x.isEvent ? "/icons/default/link.svg" : "/link_off.svg"} />
												{t("Event priority")}: {x.eventPriority ?? t("None")}
											</Tag>
											<Tag
												size="small"
												title={x.isCarFleet ? t("Is a car fleet contact") : t("Is not a car fleet contact")}
											>
												<StatusIcon src={x.isCarFleet ? "/icons/default/link.svg" : "/link_off.svg"} />
												{t("Car fleet")}: {t(x.carFleetStatus ? x.carFleetStatus : "None")}
											</Tag>
										</TagRow>
									</CompanyWrapper>
									{x.isPrimary && <StyledPrimaryLabel />}
								</ValueWrapper>
							</ViewPageSectionListItem>
						)}
					</Trigger>
				))}
				<ViewPageSectionItem>
					<Trigger
						render={(props) => (
							<ContactCompanyDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Add company")}
								initialValues={emptyContactCompany}
								onClosed={onClosed}
								onSubmit={(values) => {
									if (!values.company) throw "Missing validation for field: company";
									return contactService
										.attachCompany(contact.id, values.company.id, {
											position: values.position,
											isEvent: values.isEvent,
											isCarFleet: values.isCarFleet,
										})
										.then((res) => {
											if (isSuccess(res))
												showToast({
													body: t("Company was saved."),
													title: t("Company"),
													type: "success",
												});
											return isSuccess(res);
										});
								}}
							/>
						)}
					>
						{(props) => (
							<TextButton {...props} color="primary" prefix={<Icon $icon="plus" />}>
								{t("Add company")}
							</TextButton>
						)}
					</Trigger>
				</ViewPageSectionItem>
			</ViewPageSectionBody>
		</StyledViewPageSection>
	);
};
